import React, {useEffect, useMemo, useRef, useState} from "react";
import {Button, Divider, IconButton, Slider, Stack, Typography} from "@mui/material";
import moment from "moment";
import {grey} from "@mui/material/colors";
import {MessageBox, UserTalkingBox} from "./message-box";
import {useAtom, useSetAtom} from "jotai";
import {SelectedConversationAudioPlayer, SelectedMessageAudioPlayer} from "../atoms";
import {
  IconMessage,
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconPlayerTrackNext,
  IconPlayerTrackPrev, IconThumbUp
} from "@tabler/icons-react";
import {TOKEN} from "../../../../context/SignInContext";
import {formatTime} from "../../../../utils/functions";
import {ConversationRepository} from "../../../../repositories/ConversationRepository";
import {ConversationInteractions} from "./conversation-interactions";

export const SupervisionHistoryItem = ({
                                         conversation,
                                         messages,
                                         project,
                                         updateConversation,
                                         loadDuration = true,
                                         showDebug = true,
                                         showUserTalking = false,
                                         fetchMessages = () => {}
                                       }) => {
  const [selectedAudioConversation, setSelectedAudioConversation] = useAtom(SelectedConversationAudioPlayer);
  const setSelectedMessage = useSetAtom(SelectedMessageAudioPlayer);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [agentTalking, setAgentTalkingId] = useState(null);

  useEffect(() => {
    if (!selectedAudioConversation) {
      const audio = audioRef.current;
      if (!audio) return;

      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      }
    }
  }, [selectedAudioConversation, isPlaying])

  useEffect(() => {
    setAgentTalkingId(null);
  }, [messages?.length]);

  const handleSeek = (value) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
      if (selectedAudioConversation) {
        setSelectedAudioConversation(prev => ({...prev, currentTime: value}))
      }
    }
  };

  const getAudioUrl = (path) => {
    if (!path) return "";
    const base = project?.configuration?.conversation_logger_api || "https://api.voicebot.openbrain.io";
    const url = new URL("/api/file/audio", base);
    url.searchParams.append("path", path);
    url.searchParams.append("jwt", localStorage.getItem(TOKEN));
    return url.toString();
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      setSelectedAudioConversation({...conversation, currentTime: 0, duration: duration}); // only set when play
      setSelectedMessage(null);
      audio.play();
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    const shouldEndOrTransfer = messages?.some(msg =>
        msg?.end_message === true ||
        (msg?.transfer_number !== null && msg?.transfer_number !== "")
    );
    if (shouldEndOrTransfer && conversation?.status === "ACTIVE") {
      console.log("Conversation has ended or was transferred.");
      ConversationRepository.getConversation({id: conversation?.id})
          .then(response => {
            updateConversation(response?.data);
          })
    }
  }, [messages, conversation?.id, conversation?.status]);

  useEffect(() => {
    if (conversation?.audio) {
      const url = getAudioUrl(conversation.audio);
      const audio = new Audio(url);
      setAudioUrl(url);
      audio.preload = "metadata";
      audioRef.current = audio;

      const handleMetadata = () => {
        setDuration(audio.duration);
        setCurrentTime(0);
      };

      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
        setSelectedAudioConversation(prev => ({...prev, currentTime: audio.currentTime}));
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      audio.addEventListener("loadedmetadata", handleMetadata);
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("ended", handleEnded);

      return () => {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
        audio.removeEventListener("loadedmetadata", handleMetadata);
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("ended", handleEnded);
      };
    }
  }, [conversation?.audio, loadDuration]);

  useEffect(() => {
    if (
      selectedAudioConversation?.id !== conversation?.id &&
      audioRef.current
    ) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [selectedAudioConversation]);

  const setAgentTalking = (message, isTalking) => {
    setAgentTalkingId(prev => {
      if (isTalking) {
        // Only set if message.id is newer than current
        return (prev === null || message.id > prev) ? message.id : prev;
      } else {
        // Only clear if this message is >= current agentTalking
        return (prev !== null && message.id >= prev) ? null : prev;
      }
    });
  };


  const isLastMessage = useMemo(() => {
    if (conversation.status !== "ACTIVE") return false;

    const withoutDebugMessage = messages
      ?.filter(i => i?.role !== "DEBUG")
      ?.sort((a, b) => (a.id > b.id ? 1 : -1)); // sort by ID ascending

    const lastMessage = withoutDebugMessage?.[withoutDebugMessage.length - 1];

    return lastMessage?.last_message && lastMessage?.role === "AGENT";
  }, [conversation, messages?.length]);


  return (
    <Stack direction="column" gap={1} sx={{p: 2}}>
      <Divider
        sx={{
          width: "90%",
          mx: "auto",
          textTransform: "capitalize",
          fontSize: "0.85em",
          color: grey[600],
          fontWeight: 500
        }}
      >
        {moment(conversation?.dateCreated).format("DD MMM YYYY HH:mm")}
      </Divider>

      {messages.map((msg, index) => {
        const filtered = showDebug ? [...messages] : [...messages]?.filter(i => i?.role !== "DEBUG");
        const ind = filtered?.findIndex(i => i?.id === msg?.id);
        return (
          <MessageBox
            key={msg.id}
            message={msg}
            prevMessage={filtered[ind - 1] || null}
            project={project}
            loadDuration={loadDuration}
            showDebug={showDebug}
            setAgentTalking={setAgentTalking}
            fetchMessages={fetchMessages}
          />
        )
      })}
      {showUserTalking && isLastMessage && !agentTalking && <UserTalkingBox/>}

      {audioUrl && (
        <Stack
          direction={"column"}
          alignItems={"center"}
          maxWidth={"sm"}
          sx={{
            mx: "auto",
            width: "90%",
            p: 2,
            mt: 7,
            borderRadius: 5
          }}
          className={"border-shadow"}
        >
          <Stack direction={"row"} alignItems="center" justifyContent={"center"} gap={2} width="100%">
            <Typography variant="body2" minWidth={50}>
              {formatTime(currentTime)}
            </Typography>

            <Slider
              color="secondary"
              size={"small"}
              min={0}
              max={duration}
              step={0.1}
              value={currentTime}
              onChange={(e, newValue) => handleSeek(newValue)}
              sx={{flexGrow: 1, maxWidth: "330px"}}
            />

            <Typography variant="body2" minWidth={50}>
              {formatTime(duration)}
            </Typography>
          </Stack>

          <Stack direction={"row"} gap={2}>
            <IconButton onClick={() => handleSeek(currentTime - 5 || 0)}>
              <IconPlayerTrackPrev/>
            </IconButton>

            <IconButton onClick={togglePlay}>
              {isPlaying ? <IconPlayerPauseFilled/> : <IconPlayerPlayFilled/>}
            </IconButton>

            <IconButton onClick={() => handleSeek(currentTime + 5)}>
              <IconPlayerTrackNext/>
            </IconButton>
          </Stack>
        </Stack>
      )}

      <ConversationInteractions conversation={conversation} updateConversation={updateConversation} />

    </Stack>
  );
};
